import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { getDateFormatByLocal } from "src/utils/dateTime";

dayjs.extend(customParseFormat);

const INPUT_DATE_FORMATS = [
  "YYYYMMDD",
  "DD/MM/YYYY",
  "D/M/YYYY",
  "DD-MM-YYYY",
  "D-M-YYYY",
  "YYYY-MM-DD",
  "YYYY/MM/DD",
];

const parseDateInput = (input) => {
  if (typeof input === "number") {
    // Backward compatible:
    // old work: Unix seconds
    // new work: JS timestamp milliseconds
    return input > 9999999999 ? dayjs(input) : dayjs.unix(input);
  }

  if (input instanceof Date) {
    return dayjs(input);
  }

  if (typeof input === "string") {
    const value = input.trim();

    if (!value) return null;

    // NEW:
    // Handle YYYYMMDD before treating numeric strings as epoch.
    // Example: "20260908" -> 2026-09-08
    if (/^\d{8}$/.test(value)) {
      const parsedYYYYMMDD = dayjs(value, "YYYYMMDD", true);

      if (parsedYYYYMMDD.isValid()) {
        return parsedYYYYMMDD;
      }
    }

    // Numeric string support:
    // "1714176000" or "1714176000000"
    if (/^\d+$/.test(value)) {
      const timestamp = Number(value);

      return timestamp > 9999999999 ? dayjs(timestamp) : dayjs.unix(timestamp);
    }

    // Strict known formats
    const parsedByFormat = dayjs(value, INPUT_DATE_FORMATS, true);

    if (parsedByFormat.isValid()) {
      return parsedByFormat;
    }

    // Fallback for ISO/API dates
    return dayjs(value);
  }

  return null;
};

export const useEpochFormatter = (formatOverride) => {
  const { i18n } = useTranslation();

  const format = (input, appendFormat = "", notAppend = false) => {
    if (input === null || input === undefined || input === "") {
      return "-";
    }

    const dateFormat = formatOverride || getDateFormatByLocal(i18n.language);

    const finalFormat = notAppend
      ? appendFormat || dateFormat
      : appendFormat
        ? `${dateFormat} ${appendFormat}`
        : dateFormat;

    // NEW:
    // Handle numeric and string YYYYMMDD before the existing epoch logic.
    // This prevents "20260908" from becoming an epoch date in 1970.
    if (
      (typeof input === "string" || typeof input === "number") &&
      /^\d{8}$/.test(String(input).trim())
    ) {
      const parsedDate = dayjs(String(input).trim(), "YYYYMMDD", true);

      if (parsedDate.isValid()) {
        return parsedDate.format(finalFormat);
      }
    }

    // Existing behavior
    let date = parseDateInput(input);

    if (typeof input === "number") {
      // Detect seconds vs milliseconds
      if (input < 1e12) {
        // Unix seconds
        date = dayjs.unix(input);
      } else {
        // JS milliseconds
        date = dayjs(input);
      }
    } else if (typeof input === "string") {
      const num = Number(input);

      if (!isNaN(num)) {
        if (num < 1e12) {
          date = dayjs.unix(num);
        } else {
          date = dayjs(num);
        }
      }
    } else if (input instanceof Date) {
      date = dayjs(input);
    } else {
      return input;
    }

    if (!date || !date.isValid()) {
      return "-";
    }

    return date.format(finalFormat);
  };

  return format;
};
